import { Link } from "react-router";
import { useTranslation } from "react-i18next";
import type { MedicalTestProfileBox } from "~/features/tests/MedicalTestProfileBox";
import { MicrositeButton } from "./MicrositeButton";

export function TestOffersComponent({
  products,
  labProducts,
  pocProducts,
  header = <TestOffersComponentHeader />,
  emptyContent,
}: {
  header?: React.ReactNode;
  products?: React.ReactElement<typeof MedicalTestProfileBox>[];
  labProducts?: React.ReactNode[];
  pocProducts?: React.ReactNode[];
  emptyContent?: React.ReactNode;
}) {
  const hasProducts = Boolean(products?.length);
  const hasLabProducts = Boolean(labProducts?.length);
  const hasPocProducts = Boolean(pocProducts?.length);

  if (!hasProducts && !hasLabProducts && !hasPocProducts && !emptyContent) return null;

  const hasSplitSections = labProducts || pocProducts;

  return (
    <div className="public-container gap-8 py-8">
      {header}
      {hasSplitSections ? (
        <div className="grid grid-cols-1 gap-6">
          {labProducts?.length ? (
            <OfferSection
              badge="LAB"
              badgeClassName="text-sky-700"
              className="border-sky-100 bg-sky-50/80"
              title="Unser Testangebot für Auswertungen im Labor"
              description="Wir schicken Ihr Kapillarblut direkt in unser Fachlabor zur Auswertung. Das Ergebnis liegt in der Regel innerhalb von einer Woche vor. Der Befund wird von Ärzten anerkannt."
              items={labProducts}
              showAllTestsButton
            />
          ) : null}
          {pocProducts?.length ? (
            <OfferSection
              badge="POC"
              badgeClassName="text-emerald-700"
              className="border-emerald-100 bg-emerald-50/80"
              title="Unser Testangebot für Auswertungen bei uns vor Ort"
              description="Wir werten Ihr Kapillarblut direkt auf Analysegeräten bei uns vor Ort aus. Das Ergebnis liegt in der Regel innerhalb weniger Minuten vor."
              items={pocProducts}
            />
          ) : null}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 gap-y-10 md:grid-cols-2 lg:grid-cols-3">{products}</div>
      )}
      {!hasSplitSections && !hasProducts ? emptyContent : null}
    </div>
  );
}

const TestOffersComponentHeader = () => {
  const { t } = useTranslation();

  return (
    <header>
      <h1 className="mb-4 text-3xl font-bold tracking-tight sm:text-5xl">{t("Unser Testangebot")}</h1>
    </header>
  );
};

function OfferSection({
  badge,
  badgeClassName,
  className,
  title,
  description,
  items,
  showAllTestsButton,
}: {
  badge: string;
  badgeClassName: string;
  className: string;
  title: string;
  description: string;
  items?: React.ReactNode[];
  showAllTestsButton?: boolean;
}) {
  const { t } = useTranslation();

  return (
    <section className={`rounded-3xl border p-4 sm:p-6 ${className}`}>
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div>
          <p className={`text-sm font-semibold uppercase tracking-[0.2em] ${badgeClassName}`}>{t(badge)}</p>
          <h2 className="mt-2 text-xl font-bold tracking-tight text-slate-900 sm:text-2xl">{t(title)}</h2>
        </div>
        {showAllTestsButton ? (
          <MicrositeButton asChild>
            <Link to="products" className="inline-flex justify-center md:min-w-fit">
              {t("Alle Tests anzeigen")}
            </Link>
          </MicrositeButton>
        ) : null}
      </div>
      <p className="mt-3 text-base text-slate-600 sm:text-lg">{t(description)}</p>
      <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">{items}</div>
    </section>
  );
}
